import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { getApiUrl } from '../config/api';

const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUserFromToken } = useAuth();

  useEffect(() => {
    const completeAuth = async () => {
      try {
        // Get the authenticated user from Supabase after OAuth redirect
        // First try to get session, then verify user exists
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError || !sessionData?.session) {
          console.error('No active session found:', sessionError);
          navigate('/signin?error=Authentication failed');
          return;
        }
        
        // Now verify the user exists
        const { data: userData, error: userError } = await supabase.auth.getUser();

        if (userError || !userData?.user) {
          console.error('Supabase auth callback error - user not found:', userError);
          // Clear any invalid session data
          await supabase.auth.signOut();
          localStorage.removeItem('scanmaster_user');
          localStorage.removeItem('scanmaster_token');
          navigate('/signin?error=Authentication failed');
          return;
        }

        const user = userData.user;

        // Build a simple user object for the app
        const appUser = {
          email: user.email || '',
          name:
            user.user_metadata?.full_name ||
            user.user_metadata?.name ||
            (user.email ? user.email.split('@')[0] : '')
        };

        // Get the access token from the session we already retrieved
        const accessToken = sessionData?.session?.access_token || '';

        // Ensure user exists in users table (backup in case trigger didn't fire)
        try {
          const apiUrl = getApiUrl('user/ensure');
          const ensureResponse = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              user_id: user.id,
              email: user.email,
              name: appUser.name,
              provider: user.app_metadata?.provider || 'email',
              provider_id: user.user_metadata?.provider_id || user.id,
              avatar_url: user.user_metadata?.avatar_url || user.user_metadata?.picture
            })
          });
          
          if (!ensureResponse.ok) {
            const errorData = await ensureResponse.json().catch(() => ({ error: 'Unknown error' }));
            console.error('Failed to ensure user exists:', errorData);
            // Don't fail the auth flow - trigger might have created the user
          } else {
            const ensureData = await ensureResponse.json();
            console.log('User ensured in database:', ensureData.success ? 'success' : 'failed');
          }
        } catch (err) {
          console.error('Error ensuring user exists:', err);
          // Continue anyway - trigger should have created the user
        }

        // Persist user info
        localStorage.setItem('scanmaster_user', JSON.stringify(appUser));
        if (accessToken) {
          localStorage.setItem('scanmaster_token', accessToken);
        }

        if (setUserFromToken) {
          setUserFromToken({ ...appUser, token: accessToken });
        }

        // Redirect to dashboard
        navigate('/dashboard');
      } catch (err) {
        console.error('Error completing Supabase auth callback:', err);
        navigate('/signin?error=Authentication failed');
      }
    };

    completeAuth();
  }, [navigate, setUserFromToken]);

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh'
      }}
    >
      <div>Completing authentication...</div>
    </div>
  );
};

export default AuthCallback;
